// src/pages/AcceptQuote.jsx
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { formatDate } from '../utils/formatDate';
import InfoTooltip from '../utils/InfoTooltip';

/* ──────────────────────────────────────────────────────────────
   Same Frosting + Topper config you use elsewhere
   (kept minimal since frosting options don't add cost here)
   ────────────────────────────────────────────────────────────── */
const frostingOptions = [
  { name: 'White Buttercream', color: '#fff5f5', desc: 'Classic vanilla flavor' },
  { name: 'Cream Cheese', color: '#f0ebe7', desc: 'Rich and tangy' },
  { name: 'Chocolate Buttercream', color: '#4e342e', desc: 'Deep cocoa taste' },
  { name: 'German Chocolate Icing', color: '#6d4c41', desc: 'Nutty coconut-chocolate' },
];

const PRE_CUT_MESSAGES = [
  'Happy Birthday',
  'Happy Anniversary',
  'Congratulations',
  "It's a Boy",
  "It's a Girl",
  'Mr & Mrs',
];

// Only these toppers add money (Plain/Provided/None are $0)
const topperPriceFor = (type, textOrMsg = '') => {
  if (type === '2D Pre-Cut Message') return 15;
  if (type === 'Custom Cake Topper') {
    const words = (textOrMsg || '').trim().split(/\s+/).filter(Boolean).length;
    if (words <= 3) return 20;
    if (words === 4) return 25;
    return 30; // 5 words
  }
  return 0; // None / Plain Text / Provided
};

const topperColorFinishOptions = [
  ...['Black', 'Pink', 'Blue', 'Silver', 'Gold', 'Green', 'Maroon'].flatMap((color) => [
    { label: `Matte ${color}`, value: `Matte ${color}` },
    { label: `Glittery ${color}`, value: `Glittery ${color}` },
  ]),
  { label: 'Glittery White', value: 'Glittery White' }, // white only glittery
];

// helpers
const toAmPm = (hhmm) => {
  // hhmm = "09:30" -> "9:30 AM"
  if (!/^[0-2]?[0-9]:[0-5][0-9]$/.test(hhmm)) return hhmm;
  const [hStr, m] = hhmm.split(':');
  let h = parseInt(hStr, 10);
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${suffix}`;
};

export default function AcceptQuote() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');

  const [eventTime, setEventTime] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [canceling, setCanceling] = useState(false);

  // Customer choices
  const [customerFrosting, setCustomerFrosting] = useState('');
  const [topperType, setTopperType] = useState('');
  const [customTopperText, setCustomTopperText] = useState('');
  const [topperPreCutMsg, setTopperPreCutMsg] = useState('');
  const [topperColorFinish, setTopperColorFinish] = useState('');

  const [expanded, setExpanded] = useState(false);

  // Load the quote by token
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        console.log('[ACCEPTQUOTE] Loading quote for token:', token);
        const fromEmail = searchParams.get('src') === 'email' ? '?src=email' : '';
        const { data } = await axiosInstance.get(`/api/quote-requests/token/${token}${fromEmail}`);
        if (!mounted) return;
        console.log('[ACCEPTQUOTE] Quote loaded:', data);
        setQuote(data.quote);
        setLoading(false);
      } catch (e) {
        if (!mounted) return;
        const msg = e?.response?.data?.message || 'Failed to load quote';
        console.error('[ACCEPTQUOTE] Failed to load quote:', msg, e);
        setError(msg);
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token, searchParams]);
  // Time options (9:00–17:00 every 30 mins)
const timeOptions = useMemo(() => {
  const opts = [];
  for (let h = 9; h < 17; h++) {
    const h00 = `${h.toString().padStart(2, '0')}:00`;
    const h30 = `${h.toString().padStart(2, '0')}:30`;
    opts.push(h00, h30);
  }
  opts.push('17:00');
  return opts;
}, []);


  // Prefill customer choice UI from proposed spec (if admin allowed)
  useEffect(() => {
    if (!quote) return;
    if (quote.options?.customerCanChooseFrosting) {
      setCustomerFrosting(quote.cakeData?.frosting || '');
    }
    if (quote.options?.customerCanChooseTopper) {
      const t = quote.cakeData?.topper || '';
      // prefer showing the placeholder by leaving topperType empty when there's no topper or it's "None"
      if (!t || t === 'None') {
        setTopperType('');
      } else if (t.startsWith('Plain Text:')) {
        setTopperType('Plain Text');
        setCustomTopperText(t.replace(/^Plain Text:\s*/, '').split(' (')[0] || '');
      } else if (t.startsWith('Provided')) {
        setTopperType('Provided');
        setCustomTopperText(t.replace(/^Provided:\s*/, ''));
      } else if (t.startsWith('2D Pre-Cut Message:')) {
        setTopperType('2D Pre-Cut Message');
        setTopperPreCutMsg(t.replace(/^2D Pre-Cut Message:\s*/, '').split(' (')[0] || '');
        const cf = t.match(/\((.+)\)$/)?.[1] || '';
        setTopperColorFinish(cf);
      } else if (t.startsWith('Custom Cake Topper:')) {
        setTopperType('Custom Cake Topper');
        setCustomTopperText(t.replace(/^Custom Cake Topper:\s*/, '').split(' (')[0] || '');
        const cf = t.match(/\((.+)\)$/)?.[1] || '';
        setTopperColorFinish(cf);
      } else {
        setTopperType('Plain Text');
        setCustomTopperText(t);
      }
    }
  }, [quote]);

  // Build the topper string and adjustment
  const { customerTopper, topperAdj } = useMemo(() => {
    if (!quote?.options?.customerCanChooseTopper) {
      return { customerTopper: '', topperAdj: 0 };
    }
    let s = 'None';
    let adj = 0;
    if (topperType === 'Plain Text') {
      s = customTopperText.trim() ? `Plain Text: ${customTopperText.trim()}` : '';
      adj = topperPriceFor('Plain Text');
    } else if (topperType === 'Provided') {
      s = `Provided: ${customTopperText.trim()}`;
      adj = topperPriceFor('Provided');
    } else if (topperType === '2D Pre-Cut Message') {
      s = topperPreCutMsg
        ? `2D Pre-Cut Message: ${topperPreCutMsg}${topperColorFinish ? ` (${topperColorFinish})` : ''}`
        : '';
      adj = topperPriceFor('2D Pre-Cut Message');
    } else if (topperType === 'Custom Cake Topper') {
      const txt = customTopperText.trim();
      s = txt
        ? `Custom Cake Topper: ${txt}${topperColorFinish ? ` (${topperColorFinish})` : ''}`
        : '';
      adj = topperPriceFor('Custom Cake Topper', txt);
    } else if (topperType === 'None') {
      s = 'None';
      adj = 0;
    }
    return { customerTopper: s, topperAdj: adj };
  }, [quote?.options?.customerCanChooseTopper, topperType, customTopperText, topperPreCutMsg, topperColorFinish]);

  // Computed total (base + topper adjustment)
  const computedTotal = useMemo(() => {
    const base = Number(quote?.total || 0);
    return (base + Number(topperAdj || 0)).toFixed(2);
  }, [quote?.total, topperAdj]);

  const redirectHome = () => {
    window.location.href = 'https://candyhavenandcakes.com';
  };

// 🔹 HANDLE STRIPE PAYMENT (DEPOSIT / FULL)
const handlePayment = async (payMode) => {
  if (!quote) return;

  // --- VALIDATION ---
  if (!eventTime) {
    alert('Please select a pickup time before paying.');
    return;
  }
  if (quote?.options?.customerCanChooseFrosting && !customerFrosting.trim()) {
    alert('Please choose a frosting before paying.');
    return;
  }
  if (quote?.options?.customerCanChooseTopper) {
    if (!topperType) {
      alert('Please choose a topper option before paying.');
      return;
    }
    if (topperType === 'Plain Text' && !customTopperText.trim()) {
      alert('Please enter the cake message.');
      return;
    }
    if (topperType === '2D Pre-Cut Message') {
      if (!topperPreCutMsg) {
        alert('Please select a pre-cut message.');
        return;
      }
      if (!topperColorFinish) {
        alert('Please choose topper color & finish.');
        return;
      }
    }
    if (topperType === 'Custom Cake Topper') {
      const words = customTopperText.trim().split(/\s+/).filter(Boolean);
      if (!customTopperText.trim()) {
        alert('Please enter a custom topper message.');
        return;
      }
      if (words.length > 5) {
        alert('Custom topper message must be ≤ 5 words.');
        return;
      }
      if (!topperColorFinish) {
        alert('Please choose topper color & finish.');
        return;
      }
    }
  }

  try {
    console.log('[PAY] Starting payment', {
      payMode,
      token,
      quoteToken: quote.token,
      eventTime,
    });

    // Hit backend: POST /api/payments/quote/:token/checkout
    const { data } = await axiosInstance.post(
      `/api/payments/quote/${token}/checkout`,
      {
        payMode,
        eventTime: toAmPm(eventTime),
        customerFrosting: quote.options?.customerCanChooseFrosting ? customerFrosting : '',
        customerTopper: quote.options?.customerCanChooseTopper ? customerTopper : '',
      }
    );

    console.log('[PAY] Backend response:', data);

    if (!data?.url) {
      console.error('[PAY] Missing checkout URL:', data);
      alert(data?.message || 'Failed to start payment.');
      return;
    }

    // --- NEW: Redirect manually (Stripe.js deprecated redirectToCheckout) ---
    console.log('[PAY] Redirecting to Stripe Checkout URL:', data.url);
    window.location.href = data.url;

  } catch (error) {
    console.error('[PAY] Payment Error:', error);
    alert('Payment error: ' + (error.message || 'Unknown error'));
  }
};


  const accept = async () => {
    if (accepting || canceling) return;
    if (!eventTime) {
      alert('Please select a pickup time.');
      return;
    }
    // Validate customer choices if admin enabled them
    if (quote?.options?.customerCanChooseFrosting && !customerFrosting.trim()) {
      alert('Please choose a frosting.');
      return;
    }
    if (quote?.options?.customerCanChooseTopper) {
      if (!topperType) {
        alert('Please choose a topper option.');
        return;
      }
      if (topperType === 'Plain Text' && !customTopperText.trim()) {
        alert('Please enter the cake message.');
        return;
      }
      if (topperType === '2D Pre-Cut Message') {
        if (!topperPreCutMsg) {
          alert('Please select a pre-cut message.');
          return;
        }
        if (!topperColorFinish) {
          alert('Please choose topper color & finish.');
          return;
        }
      }
      if (topperType === 'Custom Cake Topper') {
        const words = customTopperText.trim().split(/\s+/).filter(Boolean);
        if (!customTopperText.trim()) {
          alert('Please enter a custom topper message.');
          return;
        }
        if (words.length > 5) {
          alert('Custom topper message must be ≤ 5 words.');
          return;
        }
        if (!topperColorFinish) {
          alert('Please choose topper color & finish.');
          return;
        }
      }
    }

    try {
      setAccepting(true);
      console.log('[ACCEPTQUOTE] Accepting quote with token:', token);

      await axiosInstance.post(`/api/quote-requests/token/${token}/accept`, {
        // convert 24h to AM/PM before sending
        eventTime: toAmPm(eventTime),
        // only send if allowed
        customerFrosting: quote?.options?.customerCanChooseFrosting ? customerFrosting : '',
        customerTopper: quote?.options?.customerCanChooseTopper ? customerTopper : '',
        // send pricing hints so backend can adopt it later if desired
        topperPrice: Number(topperAdj || 0),
        updatedTotal: Number(computedTotal),
      });

      alert('Order placed! We’ll email you a confirmation shortly.');
      redirectHome();
    } catch (e) {
      console.error('[ACCEPTQUOTE] Failed to accept quote:', e);
      alert(e?.response?.data?.message || 'Failed to accept quote');
      setAccepting(false);
    }
  };

  const cancel = () => {
    if (accepting || canceling) return;
    setCanceling(true);
    alert('No worries—taking you back to our home page.');
    redirectHome();
  };

  if (loading) return <div className="p-8 text-candy-primary">Loading…</div>;
  if (error) return <div className="p-8 text-red-600">{error}</div>;
  if (!quote) return <div className="p-8">Quote not found.</div>;

  const busy = accepting || canceling;
  const decorationFee = Number(quote.prices?.decoration || 0);

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6 space-y-5" aria-busy={busy}>
      {/* Header */}
      <header className="text-center space-y-1">
        <h1 className="text-2xl sm:text-3xl font-bold text-candy-primary">
          Review & Confirm Your Order
        </h1>
        <p className="text-sm text-gray-500">
          Please check everything looks good before placing the order.
        </p>
        {(quote.quoteId || quote.quoteRequestId) && (
          <div className="text-xs text-gray-400">
            {quote.quoteId && (
              <>
                Quote: <span className="font-medium">{quote.quoteId}</span>
              </>
            )}
            {quote.quoteId && quote.quoteRequestId && ' • '}
            {quote.quoteRequestId && (
              <>
                Request: <span className="font-medium">{quote.quoteRequestId}</span>
              </>
            )}
          </div>
        )}
      </header>

      {/* Summary Card */}
      <section className="bg-white/80 backdrop-blur border border-candy-border rounded-2xl p-5 shadow-sm">
        <div className="grid grid-cols-2 gap-y-2 text-sm">
          <div>
            <span className="text-gray-500">Name:</span>{' '}
            <span className="font-medium">
              {quote.firstName} {quote.lastName}
            </span>
          </div>
          <div className="text-right">
            <span className="text-gray-500">Event Date:</span>{' '}
            <span className="font-medium">{formatDate(quote.eventDate)}</span>
          </div>
          <div>
            <span className="text-gray-500">Servings:</span>{' '}
            <span className="font-medium">{quote.servings}</span>
          </div>
        </div>

        {quote.expiresAt && (
          <div className="mt-2 text-xs text-gray-500">
            Link expires: {formatDate(quote.expiresAt)}
          </div>
        )}
      </section>

      {/* Breakdown with Total */}
      {quote.prices && (
        <section className="bg-white border border-candy-border rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Price breakdown</h3>
            <div className="text-sm">
              <span className="text-gray-500 mr-1">Current total:</span>
              <span className="font-bold text-candy-primary">
                ${Number(quote.total).toFixed(2)}
              </span>
            </div>
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <li className="flex items-center justify-between border border-candy-border/60 rounded-lg px-3 py-2 bg-candy-background">
              <span className="text-gray-600">Cake</span>
              <span className="font-medium">${quote.prices.cake || 0}</span>
            </li>
            <li className="flex items-center justify-between border border-candy-border/60 rounded-lg px-3 py-2 bg-candy-background">
              <span className="text-gray-600">Decoration</span>
              <span className="font-medium">${quote.prices.decoration || 0}</span>
            </li>
            <li className="flex items-center justify-between border border-candy-border/60 rounded-lg px-3 py-2 bg-candy-background">
              <span className="text-gray-600">Filling</span>
              <span className="font-medium">${quote.prices.filling || 0}</span>
            </li>
            <li className="flex items-center justify-between border border-candy-border/60 rounded-lg px-3 py-2 bg-candy-background">
              <span className="text-gray-600">Flavor</span>
              <span className="font-medium">${quote.prices.flavor || 0}</span>
            </li>
            {topperAdj > 0 && (
              <li className="flex items-center justify-between border border-candy-border/60 rounded-lg px-3 py-2 bg-white">
                <span className="text-gray-800 font-medium">Topper</span>
                <span className="font-semibold text-candy-primary">+${topperAdj}</span>
              </li>
            )}
          </ul>

          {Number(computedTotal) !== Number(quote.total) && (
            <div className="mt-4 flex items-center justify-between rounded-xl border px-3 py-2 text-sm bg-white border-candy-border">
              <span className="text-gray-600">Updated Total (incl. topper)</span>
              <span className="font-bold text-candy-primary">${computedTotal}</span>
            </div>
          )}
        </section>
      )}

      {/* Specs & Choices */}
      {quote.cakeData && (
        <section className="bg-white border border-candy-border rounded-2xl p-5 shadow-sm space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Cake Details</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {quote.cakeData.size && (
              <div>
                <span className="text-gray-500">Size:</span>{' '}
                <span className="font-medium">{quote.cakeData.size}</span>
              </div>
            )}
            {quote.cakeData.shape && (
              <div>
                <span className="text-gray-500">Shape:</span>{' '}
                <span className="font-medium">{quote.cakeData.shape}</span>
              </div>
            )}
            {quote.cakeData.flavor && (
              <div className="sm:col-span-2">
                <span className="text-gray-500">Flavor (your choice):</span>{' '}
                <span className="font-medium">{quote.cakeData.flavor}</span>
              </div>
            )}
            {quote.cakeData.filling && (
              <div className="sm:col-span-2">
                <span className="text-gray-500">Filling (your choice):</span>{' '}
                <span className="font-medium">{quote.cakeData.filling}</span>
              </div>
            )}
            {quote.cakeData.tiers && (
              <div className="sm:col-span-2">
                <span className="text-gray-500">Tiers:</span>{' '}
                <span className="font-medium">{quote.cakeData.tiers}</span>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="bg-white border border-candy-border rounded-2xl p-5 shadow-sm space-y-2">
        {/* Frosting */}
        {!quote.options?.customerCanChooseFrosting && quote.cakeData.frosting && (
          <div className="text-sm">
            <span className="text-gray-500">Frosting:</span>{' '}
            <span className="font-medium">{quote.cakeData.frosting}</span>
          </div>
        )}
        {quote.options?.customerCanChooseFrosting && (
          <div className="mt-1">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-semibold text-gray-700">Choose Frosting</h4>
              <InfoTooltip>
                <p className="text-center text-[11px] leading-snug">
                  Select your preferred frosting for the outside of your cake.
                </p>
              </InfoTooltip>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {frostingOptions.map(({ name, color, desc }) => {
                const selected = customerFrosting === name;
                return (
                  <label
                    key={name}
                    className={`cursor-pointer p-2 rounded-lg border flex flex-col items-center text-[11px] transition-all text-center ${
                      selected
                        ? 'border-candy-primary bg-candy-background shadow-sm'
                        : 'border-candy-border bg-white hover:border-candy-primary'
                    }`}
                  >
                    <input
                      type="radio"
                      name="customerFrosting"
                      value={name}
                      checked={selected}
                      onChange={(e) => setCustomerFrosting(e.target.value)}
                      className="hidden"
                    />
                    <div
                      className="w-6 h-4 rounded-full border border-gray-400 mb-1"
                      style={{ backgroundColor: color }}
                    />
                    <div className="font-medium leading-tight">{name}</div>
                    <div className="text-[10px] text-gray-500">{desc}</div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Topper */}
        {!quote.options?.customerCanChooseTopper && quote.cakeData.topper && (
          <div className="text-sm">
            <span className="text-gray-500">Topper:</span>{' '}
            <span className="font-medium">{quote.cakeData.topper}</span>
          </div>
        )}
        {quote.options?.customerCanChooseTopper && (
          <div className="mt-2">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-semibold text-gray-700">Cake Message / Topper</h4>
              <InfoTooltip>
                <p className="text-center text-[11px] leading-snug">
                  Choose a message option. Some options (pre-cut/custom) add a small cost and
                  will update your total.
                </p>
              </InfoTooltip>
            </div>

            <select
              value={topperType}
              onChange={(e) => {
                const val = e.target.value;
                setTopperType(val);
                // reset sub-fields on change
                setCustomTopperText('');
                setTopperPreCutMsg('');
                setTopperColorFinish('');
              }}
              className="appearance-none w-full max-w-md p-2.5 border border-candy-border rounded-lg text-sm bg-white shadow-sm"
            >
              <option value="" disabled hidden>
                Select a cake topper/message
              </option>
              <option value="None">None</option>
              <option value="Plain Text">Plain Text (hand written in icing)</option>
              <option value="Provided">Provided by Customer</option>
              <option value="2D Pre-Cut Message">2D Pre-Cut Message (+$15)</option>
              <option value="Custom Cake Topper">Custom 2D Message (Starts at $20)</option>
            </select>

            {topperType === 'Plain Text' && (
              <>
                <input
                  type="text"
                  maxLength={40}
                  value={customTopperText}
                  placeholder="e.g. Happy 18th Birthday Sam"
                  onChange={(e) => setCustomTopperText(e.target.value)}
                  className="mt-2 w-full max-w-md p-2 border rounded-md text-sm"
                />
                <div className="mt-1 text-xs text-gray-500">
                  {customTopperText.length}/40 characters
                </div>
              </>
            )}

            {topperType === 'Provided' && (
              <textarea
                rows={3}
                placeholder="Instructions (Optional) e.g. Please leave topper on the side."
                value={customTopperText}
                onChange={(e) => setCustomTopperText(e.target.value)}
                className="mt-2 w-full max-w-md p-2 border rounded-md text-sm"
              />
            )}

            {topperType === '2D Pre-Cut Message' && (
              <>
                <select
                  value={topperPreCutMsg}
                  onChange={(e) => setTopperPreCutMsg(e.target.value)}
                  className="mt-2 appearance-none w-full max-w-md p-2.5 border border-candy-border rounded-lg text-sm bg-white shadow-sm"
                >
                  <option value="" disabled hidden>
                    Select a message
                  </option>
                  {PRE_CUT_MESSAGES.map((msg) => (
                    <option key={msg} value={msg}>
                      {msg}
                    </option>
                  ))}
                </select>
                <div className="mt-3">
                  <div className="mb-1 text-xs font-medium">Topper Color &amp; Finish</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {topperColorFinishOptions.map((opt) => {
                      const isSel = topperColorFinish === opt.value;
                      return (
                        <button
                          type="button"
                          key={opt.value}
                          onClick={() => setTopperColorFinish(opt.value)}
                          className={`w-full h-10 rounded-md text-xs border transition ${
                            isSel
                              ? 'border-candy-primary bg-candy-background'
                              : 'border-gray-300 hover:border-candy-primary'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {topperType === 'Custom Cake Topper' && (
              <>
                <input
                  type="text"
                  maxLength={50}
                  value={customTopperText}
                  placeholder="e.g. Riya’s Sweet 16 (≤5 words)"
                  onChange={(e) => setCustomTopperText(e.target.value)}
                  className="mt-2 w-full max-w-md p-2 border rounded-md text-sm"
                />
                <div className="mt-1 text-xs text-gray-500">
                  {customTopperText.trim().split(/\s+/).filter(Boolean).length}/5 words
                </div>
                <div className="mt-3">
                  <div className="mb-1 text-xs font-medium">Topper Color &amp; Finish</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {topperColorFinishOptions.map((opt) => {
                      const isSel = topperColorFinish === opt.value;
                      return (
                        <button
                          type="button"
                          key={opt.value}
                          onClick={() => setTopperColorFinish(opt.value)}
                          className={`w-full h-10 rounded-md text-xs border transition ${
                            isSel
                              ? 'border-candy-primary bg-candy-background'
                              : 'border-gray-300 hover:border-candy-primary'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* sticky-ish adjustment chip */}
            <div className="mt-3">
              <div className="inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 text-sm bg-white border-candy-border">
                <span className="text-gray-600">Topper:</span>
                <span className="font-semibold">{topperType || '—'}</span>
                <span className="text-gray-400">|</span>
                <span className="text-gray-600">Adj:</span>
                <span
                  className={`font-bold ${
                    topperAdj > 0 ? 'text-candy-primary' : 'text-gray-700'
                  }`}
                >
                  ${topperAdj || 0}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Pickup time */}
        <label className="block text-sm font-medium text-candy-primary">
          Preferred Pickup Time <span className="text-red-500">*</span>
        </label>
        <select
  value={eventTime}
  onChange={(e) => setEventTime(e.target.value)}
  className="px-3 py-2 border rounded w-full max-w-xs disabled:opacity-60"
  required
  disabled={busy}
>
  <option value="">Select a time</option>
  {timeOptions.map((t) => (
    <option key={t} value={t}>
      {toAmPm(t)}
    </option>
  ))}
</select>

        <p className="text-[11px] text-gray-500">Pickup window is 9:00 AM – 5:00 PM.</p>

        {/* Additional notes (only if admin allowed) */}
        {quote.options?.customerCanSeeAdditional && quote.cakeData.additional && (
          <div className="pt-2 border-t text-sm">
            <span className="text-gray-500">Notes:</span>{' '}
            <span className="font-medium">{quote.cakeData.additional}</span>
          </div>
        )}
      </section>

      {/* Action bar */}
      <div className="sticky bottom-3 z-10">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between bg-white/90 backdrop-blur rounded-xl border border-candy-border p-3 shadow-sm">
          <div className="text-sm">
            <span className="text-gray-500">Total:</span>{' '}
            <span className="text-lg font-extrabold text-candy-primary">${computedTotal}</span>
            {Number(computedTotal) !== Number(quote.total) && (
              <span className="ml-2 text-xs text-gray-500">(updated)</span>
            )}
          </div>


<div className="flex flex-col gap-3">

  {/* ──────────────────────────────────────────── */}
  {/* PRIMARY BUTTON: PLACE ORDER */}
  {/* ──────────────────────────────────────────── */}
  <button
    onClick={() => setExpanded((prev) => !prev)}
    disabled={busy}
    className="bg-candy-primary text-white px-6 py-3 rounded-lg hover:bg-candy-primaryDark"
  >
    {expanded ? 'Hide Options' : 'Place Order'}
  </button>

  {/* ──────────────────────────────────────────── */}
  {/* EXPANDED PAYMENT OPTIONS */}
  {/* ──────────────────────────────────────────── */}
  {expanded && (
    <div className="flex flex-col gap-3 border p-4 rounded-lg bg-gray-50">

      {/* CASE 1 ➜ decorationFee < 25 */}
      {decorationFee < 25 ? (
        <>
          <button
            onClick={() => handlePayment('full')}
            className="bg-green-600 text-white px-5 py-3 rounded-lg"
          >
            Pay Full Amount (${Number(computedTotal).toFixed(2)})
          </button>

<button
  onClick={accept}
  disabled={busy || accepting}
  className={`
    px-5 py-3 rounded-lg text-white transition
    ${busy || accepting
      ? 'bg-yellow-600/60 cursor-not-allowed'
      : 'bg-yellow-600 hover:bg-yellow-700'}
  `}
>
  {(busy || accepting) ? 'Processing…' : 'Pay at pickup'}
</button>

        </>
      ) : (
        /* CASE 2 ➜ decorationFee >= 25 */
        <>
          <button
            onClick={() => handlePayment('deposit')}
            className="bg-yellow-600 text-white px-5 py-3 rounded-lg"
          >
            Pay Minimum Due (${decorationFee.toFixed(2)})
          </button>

          <button
            onClick={() => handlePayment('full')}
            className="bg-green-600 text-white px-5 py-3 rounded-lg"
          >
            Pay Full Amount (${Number(computedTotal).toFixed(2)})
          </button>

          {/* 🚫 Pay Later removed when decorationFee >= 25 */}
        </>
      )}
    </div>
  )}

  {/* CANCEL BUTTON */}
  <button
    onClick={cancel}
    disabled={busy}
    className={`px-4 py-2 rounded-lg border border-candy-border bg-white transition ${
      canceling ? 'opacity-70 cursor-not-allowed' : 'hover:bg-candy-background'
    }`}
  >
    {canceling ? 'Canceling…' : 'Cancel'}
  </button>
</div>

        </div>
      </div>
    </div>
  );
}
